import * as React from 'react';
import {
  PropsWithChildren,
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import {ThemeProvider} from '@mui/material/styles';

import {darkTheme, digiCuteTheme, lightTheme} from '../theme';
import {getValue, setValue} from '../utils';

const noop = () => {};

const allColorModes = ['lightTheme', 'darkTheme', 'digiCuteTheme'];
export type ColorMode = (typeof allColorModes)[number];

interface ColorModeContextType {
  handleColorChange: () => void;
  colorMode: ColorMode;
  colorChangeLabel: string;
  isLightMode: boolean;
  isDarkMode: boolean;
  isDigiCute: boolean;
}

const ColorModeContext = createContext<ColorModeContextType>({
  handleColorChange: noop,
  colorMode: 'lightTheme',
  colorChangeLabel: 'Dark Mode',
  isLightMode: true,
  isDarkMode: false,
  isDigiCute: false,
});

export const useColorMode = () => React.useContext(ColorModeContext);

interface ToggleColorModeProps {
  active: boolean;
}

const themeMap = {
  lightTheme,
  darkTheme,
  digiCuteTheme,
} as const;

const loadColorMode = (): ColorMode => {
  const localStorageColorMode = getValue('mode', 'lightTheme') as ColorMode;
  if (allColorModes.includes(localStorageColorMode)) {
    return localStorageColorMode;
  }
  return 'lightTheme';
};

export const ToggleColorMode: React.FC<
  PropsWithChildren<ToggleColorModeProps>
> = ({active, children}) => {
  const [mode, setMode] = useState<ColorMode>(loadColorMode());
  const theme = useMemo(() => {
    return active ? themeMap[mode] : lightTheme;
  }, [mode, active]);

  const colorChangeLabel = useMemo(() => {
    switch (mode) {
      case 'lightTheme':
        return 'Dark Mode';
      case 'darkTheme':
        return 'Light Mode';
      case 'digiCuteTheme':
        return 'Digi-Cute';
      default:
        return 'Dark Mode';
    }
  }, [mode]);

  const isDigiCute = useMemo(() => mode === 'digiCuteTheme', [mode]);
  const isLightMode = useMemo(() => mode === 'lightTheme', [mode]);
  const isDarkMode = useMemo(() => mode === 'darkTheme', [mode]);

  // ─── EasterEgg: 5x zwischen Light/Dark wechseln, um Digi-Cute freizuschalten ─
  const toggleCount = React.useRef(0);
  const toggleTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timer-Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (toggleTimer.current) clearTimeout(toggleTimer.current);
    };
  }, []);

  const handleColorChange = useCallback(() => {
    if (isDigiCute) {
      // Digi-Cute verlassen → zurück zu Light, Zähler zurücksetzen
      toggleCount.current = 0;
      const newMode: ColorMode = 'lightTheme';
      setMode(newMode);
      setValue('mode', newMode);
      return;
    }

    // Schnell-Umschalter innerhalb von 6 Sekunden zählen
    toggleCount.current += 1;

    if (toggleCount.current === 1) {
      toggleTimer.current = setTimeout(() => {
        toggleCount.current = 0;
      }, 6000);
    }

    if (toggleCount.current >= 5) {
      // Digi-Cute freischalten!
      toggleCount.current = 0;
      if (toggleTimer.current) clearTimeout(toggleTimer.current);
      const newMode: ColorMode = 'digiCuteTheme';
      setMode(newMode);
      setValue('mode', newMode);
      return;
    }

    // Normaler Wechsel: Light ↔ Dark
    const newMode: ColorMode =
      mode === 'darkTheme' ? 'lightTheme' : 'darkTheme';
    setMode(newMode);
    setValue('mode', newMode);
  }, [mode, isDigiCute]);

  const colorModeContextValue = useMemo(
    () => ({
      handleColorChange,
      colorMode: mode,
      colorChangeLabel,
      isDigiCute,
      isDarkMode,
      isLightMode,
    }),
    [
      mode,
      colorChangeLabel,
      isDarkMode,
      isLightMode,
      isDigiCute,
      handleColorChange,
    ],
  );

  return (
    <ColorModeContext.Provider value={colorModeContextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline key={mode} enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export {ColorModeContext};
